import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import FrameSizeIcon from "./components/FrameSizeIcon";
import API_BASE_URL from "./config";
import "./PhotoFrameOrder.css";
import submitPaymentForm from "./desktop/submitPaymentForm";

const EMPTY_ADDRESS = {
  guestName: "", guestEmail: "", guestPhone: "",
  shippingAddressLine1: "", shippingAddressLine2: "",
  shippingCity: "", shippingState: "", shippingPostalCode: "", shippingCountry: "India",
};

function PhotoFrameOrder() {
  const { token, imageId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [step, setStep] = useState("size"); // "size" | "address"
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [shippingQuote, setShippingQuote] = useState(null); // { zone, shippingCost } | null
  const [shippingLoading, setShippingLoading] = useState(false);
  const formRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [imgRes, optRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/qr/${token}/images/${imageId}`),
        fetch(`${API_BASE_URL}/api/qr/${token}/photo-frame-options`),
      ]);
      if (imgRes.ok) {
        const imgData = await imgRes.json();
        setImageUrl(imgData.image_url);
      }
      if (!optRes.ok) {
        setError("Photo frames aren't available for this gallery.");
        return;
      }
      const optData = await optRes.json();
      setOptions(optData.options || []);
      if (optData.options?.length) setSelectedId(optData.options[0].frameDimensionId);
    } catch {
      setError("Failed to load frame options. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token, imageId]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = options.find((o) => o.frameDimensionId === selectedId);

  const updateAddress = (field, value) => setAddress((prev) => ({ ...prev, [field]: value }));

  // Shipping depends on the guest's destination, so it's only knowable once they've
  // entered state + country — fetched fresh (debounced) as those fields settle.
  useEffect(() => {
    if (step !== "address" || !selectedId || !address.shippingState.trim() || !address.shippingCountry.trim()) {
      setShippingQuote(null);
      return;
    }
    setShippingLoading(true);
    const t = setTimeout(async () => {
      try {
        const qs = new URLSearchParams({
          frameDimensionId: selectedId,
          state: address.shippingState.trim(),
          country: address.shippingCountry.trim(),
        });
        const res = await fetch(`${API_BASE_URL}/api/qr/${token}/photo-frame-shipping-cost?${qs.toString()}`);
        if (res.ok) setShippingQuote(await res.json());
      } catch {
        // Non-fatal — the server recomputes shipping authoritatively at order creation anyway.
      } finally {
        setShippingLoading(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [step, selectedId, address.shippingState, address.shippingCountry, token]);

  const grandTotal = selected ? selected.totalPrice + (shippingQuote?.shippingCost || 0) : 0;

  const addressValid =
    address.guestName.trim() && address.guestPhone.trim() && address.shippingAddressLine1.trim() &&
    address.shippingCity.trim() && address.shippingState.trim() && address.shippingPostalCode.trim();

  const handlePlaceOrder = async () => {
    if (!addressValid || !selected) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr/${token}/photo-frame-orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageId: parseInt(imageId), frameDimensionId: selectedId, ...address }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to place order");

      submitPaymentForm(formRef.current, data.payuUrl, data.params);
    } catch (err) {
      setSubmitError(err.message || "Failed to place order. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="pfo-container">
      <div className="pfo-card">
        <h1 className="pfo-title">🖼️ Make it a Photo Frame</h1>
        <p className="pfo-subtitle">Surprise your loved one — turn this photo into a framed print, delivered to your door.</p>

        {loading ? (
          <p className="pfo-loading">Loading...</p>
        ) : error ? (
          <p className="pfo-error">{error}</p>
        ) : (
          <>
            <div className="pfo-preview">
              {imageUrl && <img src={imageUrl} alt="Selected" className="pfo-preview-img" />}
            </div>

            {options.length === 0 ? (
              <p className="pfo-error">This studio hasn't set up any frame sizes yet.</p>
            ) : step === "size" ? (
              <>
                <h3 className="pfo-section-title">Choose a size</h3>
                <div className="pfo-size-grid">
                  {options.map((o) => (
                    <button
                      key={o.frameDimensionId}
                      className={`pfo-size-card ${selectedId === o.frameDimensionId ? "selected" : ""}`}
                      onClick={() => setSelectedId(o.frameDimensionId)}
                    >
                      <FrameSizeIcon widthInches={o.widthInches} heightInches={o.heightInches} />
                      <div className="pfo-size-label">{o.label}</div>
                      <div className="pfo-size-dims">{o.widthInches}" × {o.heightInches}"</div>
                      <div className="pfo-size-price">₹{o.totalPrice.toFixed(0)}</div>
                    </button>
                  ))}
                </div>

                <p className="pfo-print-note">Printed from your uploaded photo resolution.</p>

                <button className="pfo-continue-btn" disabled={!selected} onClick={() => setStep("address")}>
                  Continue{selected ? ` — ₹${selected.totalPrice.toFixed(0)}` : ""}
                </button>
              </>
            ) : (
              <>
                <button className="pfo-back-btn" onClick={() => setStep("size")}>← Back to sizes</button>
                <h3 className="pfo-section-title">Shipping details</h3>
                <div className="pfo-order-summary">
                  {selected.label} ({selected.widthInches}" × {selected.heightInches}") — ₹{selected.totalPrice.toFixed(0)}
                </div>

                <div className="pfo-form">
                  <input className="pfo-input" placeholder="Full name" value={address.guestName} onChange={(e) => updateAddress("guestName", e.target.value)} />
                  <input className="pfo-input" placeholder="Phone number" value={address.guestPhone} onChange={(e) => updateAddress("guestPhone", e.target.value)} />
                  <input className="pfo-input" placeholder="Email (optional, for order updates)" value={address.guestEmail} onChange={(e) => updateAddress("guestEmail", e.target.value)} />
                  <input className="pfo-input" placeholder="Address line 1" value={address.shippingAddressLine1} onChange={(e) => updateAddress("shippingAddressLine1", e.target.value)} />
                  <input className="pfo-input" placeholder="Address line 2 (optional)" value={address.shippingAddressLine2} onChange={(e) => updateAddress("shippingAddressLine2", e.target.value)} />
                  <div className="pfo-form-row">
                    <input className="pfo-input" placeholder="City" value={address.shippingCity} onChange={(e) => updateAddress("shippingCity", e.target.value)} />
                    <input className="pfo-input" placeholder="State" value={address.shippingState} onChange={(e) => updateAddress("shippingState", e.target.value)} />
                  </div>
                  <div className="pfo-form-row">
                    <input className="pfo-input" placeholder="Postal code" value={address.shippingPostalCode} onChange={(e) => updateAddress("shippingPostalCode", e.target.value)} />
                    <input className="pfo-input" placeholder="Country" value={address.shippingCountry} onChange={(e) => updateAddress("shippingCountry", e.target.value)} />
                  </div>
                </div>

                <div className="pfo-cost-breakdown">
                  <div className="pfo-cost-row">
                    <span>{selected.label} frame</span>
                    <span>₹{selected.totalPrice.toFixed(0)}</span>
                  </div>
                  <div className="pfo-cost-row">
                    <span>Shipping</span>
                    <span>
                      {!address.shippingState.trim() || !address.shippingCountry.trim()
                        ? "Enter address"
                        : shippingLoading
                        ? "Calculating..."
                        : shippingQuote
                        ? shippingQuote.shippingCost > 0 ? `₹${shippingQuote.shippingCost.toFixed(0)}` : "Free"
                        : "—"}
                    </span>
                  </div>
                  <div className="pfo-cost-row pfo-cost-row--total">
                    <span>Total</span>
                    <span>₹{grandTotal.toFixed(0)}</span>
                  </div>
                </div>

                {submitError && <p className="pfo-error">{submitError}</p>}

                <button className="pfo-continue-btn" disabled={!addressValid || submitting} onClick={handlePlaceOrder}>
                  {submitting ? "Redirecting to payment..." : `Pay ₹${grandTotal.toFixed(0)}`}
                </button>
              </>
            )}
          </>
        )}

        <form ref={formRef} style={{ display: "none" }} />
      </div>
    </div>
  );
}

export default PhotoFrameOrder;
